import {
  CheckOutlined,
  CloseOutlined,
  EllipsisOutlined
} from '@ant-design/icons';
import { Dropdown, MenuProps, theme } from 'antd';
import { ActionTag } from 'components/action-tag/action-tag-component';
import { BaseButton } from 'components/button/button-base';
import { useCallback, useState, CSSProperties } from 'react';
import {
  BaseTableUserRecord,
  TableRow,
  UserListProps
} from '../user-table-types';

interface ActionDropdownProps<T extends BaseTableUserRecord> {
  actions?: UserListProps<T>['actions'];
  onError?: UserListProps<T>['onError'];
  record: TableRow<T>;
  reloadData: () => void;
}

type Status = 'success' | 'error';
function ActionDropdown<T extends BaseTableUserRecord>({
  actions,
  onError,
  record,
  reloadData
}: ActionDropdownProps<T>) {
  const { token } = theme.useToken();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<Status | null>(null);

  const clearStatus = () => {
    setStatus(null);
  };

  const setStatusWithTimeout = (
    type: Status,
    timeout: number = 3000
  ) => {
    setStatus(type);
    setTimeout(() => {
      clearStatus();
    }, timeout);
  };

  const visibleOptions = (actions ?? []).filter(action => {
    if (
      action.shouldShow === false ||
      (typeof action.shouldShow === 'function' && !action.shouldShow(record))
    ) {
      return false;
    }
    return true;
  });

  const tags: MenuProps['items'] = visibleOptions.map((action, index) => {
    return {
      key: `action-${index}-${action.name}`,
      label: (
        <ActionTag
          action={{
            ...action,
            onClick: (...args) => {
              setIsLoading(true);
              clearStatus();
              return action.onClick(...args);
            },
            onSuccess: () => {
              setIsLoading(false);
              setStatusWithTimeout('success');
              return action.onSuccess?.(reloadData);
            },
            onError: (...args) => {
              setIsLoading(false);
              setStatusWithTimeout('error');
              action.onError?.(...args);
              onError?.(...args);
            }
          }}
          record={record}
        />
      )
    };
  });

  tags.unshift(
    {
      key: 'header',
      label: 'Actions',
      disabled: true
    },
    {
      key: 'userId',
      label: `User ID: ${record.id}`,
      disabled: true
    }
  );

  let triggerIcon = <EllipsisOutlined />;
  let buttonStyle: CSSProperties = {};

  if (status === 'success') {
    triggerIcon = <CheckOutlined style={{ color: '#fff' }} />;
    buttonStyle = {
      backgroundColor: token.colorSuccess,
      borderColor: token.colorSuccess
    };
  } else if (status === 'error') {
    triggerIcon = <CloseOutlined style={{ color: '#fff' }} />;
    buttonStyle = {
      backgroundColor: token.colorError,
      borderColor: token.colorError
    };
  }

  return (
    <Dropdown
      menu={{
        items: tags
      }}
      trigger={['click']}
      arrow
    >
      <BaseButton loading={isLoading} style={buttonStyle}>
        {triggerIcon}
      </BaseButton>
    </Dropdown>
  );
}

/**
 * Returns a memoized function that returns a list of actions that can be provided to a table column render function
 * @param props
 * @param reloadData
 * @returns
 */
export function useResolveActionsColumnData<T extends BaseTableUserRecord>(
  props: Pick<UserListProps<T>, 'actions' | 'onError'>,
  reloadData: () => void
) {
  return useCallback(
    (_value: unknown, record: TableRow<T>) => (
      <ActionDropdown
        actions={props.actions}
        onError={props.onError}
        record={record}
        reloadData={reloadData}
      />
    ),
    [props.actions, props.onError, reloadData]
  );
}